import { Fragment } from 'react';
import {
  Alert,
  Button,
  Container,
  Divider,
  Paper,
  Snackbar,
  Stack,
  Typography,
  useTheme,
  Box,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AllAccessSettingsSection, SpirisConnectionProfilesSection } from '../features/admin/components/SpirisConnectionSections';
import { useManageSpirisConnections } from '../features/admin/hooks/useManageSpirisConnections';

type Props = {
  notice?: string | null;
  onOpenReport?: (profileKey: string) => void;
};

export default function ManageSpirisConnectionsPage({ notice, onOpenReport }: Props) {
  const theme = useTheme();
  const {
    allAccessGroup,
    allAccessUpdatedAt,
    allAccessUpdatedBy,
    error,
    flash,
    hasAllAccessGroupChanges,
    isLoading,
    isSavingSettings,
    items,
    newProfileKey,
    setAllAccessGroup,
    setFlash,
    setNewProfileKey,
    handleConnect,
    handleCreateProfile,
    handleCreateReport,
    handleDisconnect,
    handleSaveAllAccessGroup,
    load,
  } = useManageSpirisConnections({ onOpenReport });

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: '100%',
        backgroundColor: theme.palette.background.default,
        p: 2,
      }}
    >
      <Container maxWidth="md">
        <Paper elevation={6} sx={{ p: { xs: 3, sm: 4 }, borderRadius: '16px' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Hantera Spiris-kopplingar
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
              <Button
                onClick={load}
                variant="outlined"
                startIcon={<RefreshIcon />}
                disabled={isLoading}
                sx={{
                  textTransform: 'none',
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.common.white,
                  borderColor: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                    borderColor: theme.palette.primary.dark,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                    borderColor: theme.palette.primary.main,
                    opacity: 0.6,
                  },
                }}
              >
                Uppdatera
              </Button>
            </Box>
          </Box>

          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 1 }}>
            Profiler per Workspace‑grupp.
          </Typography>

          {notice && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                backgroundColor: theme.palette.warning.light,
                color: theme.palette.warning.contrastText,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {notice}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Fragment>
            <Snackbar
              open={Boolean(flash)}
              autoHideDuration={4000}
              onClose={() => setFlash(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <Alert
                onClose={() => setFlash(null)}
                severity={flash?.severity || 'success'}
                variant="filled"
                sx={{ width: '100%' }}
              >
                {flash?.message}
              </Alert>
            </Snackbar>

            <Stack spacing={2}>
              <AllAccessSettingsSection
                allAccessGroup={allAccessGroup}
                allAccessUpdatedAt={allAccessUpdatedAt}
                allAccessUpdatedBy={allAccessUpdatedBy}
                disabled={isLoading}
                hasChanges={hasAllAccessGroupChanges}
                isSaving={isSavingSettings}
                onChange={setAllAccessGroup}
                onSave={handleSaveAllAccessGroup}
              />

              {error && <Alert severity="error">{error}</Alert>}

              <SpirisConnectionProfilesSection
                isLoading={isLoading}
                newProfileKey={newProfileKey}
                profiles={items}
                onChangeNewProfileKey={setNewProfileKey}
                onConnect={handleConnect}
                onCreateProfile={handleCreateProfile}
                onCreateReport={handleCreateReport}
                onDisconnect={handleDisconnect}
              />
            </Stack>
          </Fragment>
        </Paper>
      </Container>
    </Box>
  );
}
